import axios, {
  AxiosResponse,
  AxiosRequestConfig,
  AxiosInstance,
  AxiosError,
  Method,
} from 'axios';
import * as Sentry from '@sentry/react';

class ApiServiceError implements AxiosError {
  name = 'ApiServiceError';
  message: string;
  config: AxiosRequestConfig = {};
  response: AxiosResponse;
  isAxiosError = true;
  toJSON: () => Record<string, unknown> = () => ({});

  // TODO messageはresponse.statusText("Forbidden")を受け取るようになっていて、
  // 情報のないメッセージになっているので、V1で定義しているメッセージ型を使っても良いのかも
  constructor(message: string, response: AxiosResponse) {
    this.message = message;
    this.response = response;
  }
}

const DOMAIN = {
  USERSITE: 'usersite',
  ADMIN: 'admin',
};
type Domain = typeof DOMAIN[keyof typeof DOMAIN];

abstract class ApiService {
  apiClient: AxiosInstance;
  domain: Domain = DOMAIN.USERSITE;

  METHOD: {
    GET: Method;
    POST: Method;
    DELETE: Method;
    PUT: Method;
    PATCH: Method;
  } = {
    GET: 'GET',
    POST: 'POST',
    DELETE: 'DELETE',
    PUT: 'PUT',
    PATCH: 'PATCH',
  };
  DOMAIN = DOMAIN;

  constructor() {
    this.apiClient = this.createApiClient();

    // レスポンスの共通処理を記述.
    this.apiClient.interceptors.response.use(
      (response: AxiosResponse) => {
        const { data } = response;
        if (
          data !== null &&
          typeof data === 'object' &&
          data.constructor === Object
        ) {
          return { ...response, data };
        }
        return response;
      },
      (error: AxiosError) => {
        if (this.reportToSentry(error)) {
          Sentry.captureException(error);
        }

        return Promise.reject(error);
      },
    );
  }

  private getBaseUrl(domain?: Domain): string {
    if (this.domain === this.DOMAIN.ADMIN) {
      return process.env.ADMIN_HOST as string;
    }
    return process.env.FRONT_HOST as string;
  }

  // domainをadminに変更する場合、下記のコードをapiServiceのconstructorに入れる
  // this.setDomain(this.DOMAIN.ADMIN);
  private setBaseUrl(): void {
    this.apiClient.defaults.baseURL = this.getBaseUrl(this.domain);
  }

  protected setDomain(domain: Domain): void {
    this.domain = domain;
    this.setBaseUrl();
  }

  // axiosベースのapiClientのインスタンスを作る
  private createApiClient(): AxiosInstance {
    return axios.create({
      baseURL: this.getBaseUrl(),
      timeout: 30000,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-API-KEY': process.env.API_KEY || '',
      },
      responseType: 'json',
    });
  }

  // Apiの呼び出しを行う
  protected async request<T>(
    url: string,
    method: Method = this.METHOD.GET,
    optionConfig: AxiosRequestConfig = {},
  ): Promise<T> {
    const baseConfig = {
      url,
      method,
      ...optionConfig,
    };

    const config = await this.setAuthRequestHeader(baseConfig);
    return this.apiClient
      .request(config)
      .then(this.handleResponse)
      .then(this.parseResponse);
  }

  // Apiの呼び出しを仮で行い、0.5秒後に受け取ったモックデータを返す
  protected async requestMock<T>(
    url: string,
    method: string = this.METHOD.POST,
    mockResponse: T | Array<T>,
  ): Promise<T | Array<T>> {
    await new Promise((resolve) =>
      setTimeout(() => resolve({ url, method }), 500),
    );
    return mockResponse;
  }

  private handleResponse<T>(response: AxiosResponse<T>): AxiosResponse<T> {
    if (response.status >= 200 && response.status < 300) {
      return response;
    }

    const error = new ApiServiceError(response.statusText, response);
    throw error;
  }

  private parseResponse<T>(
    response: AxiosResponse<T>,
  ): T | Record<string, unknown> {
    return response.data || {};
  }

  // JWT認証をする場合、このメソッドに追加する
  private async setAuthRequestHeader(
    config: AxiosRequestConfig,
  ): Promise<AxiosRequestConfig> {
    // const access = await localStorage.getItem('access');
    // if (!access) return config;

    return Object.assign(config, {
      headers: {
        ...config.headers,
        // Authorization: `JWT ${access}`,
      },
    });
  }

  // エラー内容を Sentry へレポートするかどうか
  protected reportToSentry(error: AxiosError): boolean {
    return error.response?.status === 422;
  }
}

export default ApiService;
